'use client'

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Download, Printer, Calculator, Info, Plus } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import InfoIcmsExclusao from './info-icms-exclusao'

interface CompensacaoItem {
  pa: string
  credito: string
  valor: number
  perDcomp: string
}

const dadosIniciais = {
  pis: [
    {
      pa: '11/2024',
      credito: 'PIS/PASEP',
      valor: 1414.22,
      perDcomp: '18352.53597.150425.1.3.04-8815'
    }
  ],
  cofins: [
    {
      pa: '11/2024',
      credito: 'COFINS',
      valor: 931.73,
      perDcomp: '00746.77076.150425.1.3.04-0860'
    },
    {
      pa: '12/2024',
      credito: 'COFINS',
      valor: 2371.29,
      perDcomp: '13245.74588.150425.1.3.04-9000'
    }
  ],
  irpj: [
    {
      pa: '10/2024',
      credito: 'IRPJ',
      valor: 1500.0,
      perDcomp: '12345.67890.150425.1.3.04-1234'
    }
  ],
  csll: [
    {
      pa: '10/2024',
      credito: 'CSLL',
      valor: 800.0,
      perDcomp: '98765.43210.150425.1.3.04-5678'
    }
  ]
}

export function RelatorioCompensacao() {
  const [exportando, setExportando] = useState(false)
  const [dados, setDados] = useState(dadosIniciais)
  const [modalAberto, setModalAberto] = useState(false)
  const [tipoSelecionado, setTipoSelecionado] = useState<string>('')
  const [novoItem, setNovoItem] = useState({
    pa: '',
    credito: '',
    valor: '',
    perDcomp: ''
  })

  const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor)
  }

  const calcularTotal = (items: CompensacaoItem[]) => {
    return items.reduce((acc, item) => acc + item.valor, 0)
  }

  const totalPis = calcularTotal(dados.pis)
  const totalCofins = calcularTotal(dados.cofins)
  const totalIrpj = calcularTotal(dados.irpj)
  const totalCsll = calcularTotal(dados.csll)
  const totalGeral = totalPis + totalCofins + totalIrpj + totalCsll

  const exportarRelatorio = () => {
    setExportando(true)
    setTimeout(() => {
      setExportando(false)
      alert('Relatório exportado com sucesso!')
    }, 1500)
  }

  const abrirModal = (tipo: string) => {
    setTipoSelecionado(tipo)
    setNovoItem({
      pa: '',
      credito: tipo.toUpperCase(),
      valor: '',
      perDcomp: ''
    })
    setModalAberto(true)
  }

  const adicionarItem = () => {
    if (!novoItem.pa || !novoItem.valor || !novoItem.perDcomp) {
      alert('Por favor, preencha todos os campos')
      return
    }

    const item: CompensacaoItem = {
      pa: novoItem.pa,
      credito: novoItem.credito,
      valor: Number.parseFloat(novoItem.valor),
      perDcomp: novoItem.perDcomp
    }

    const tipoKey = tipoSelecionado.toLowerCase() as keyof typeof dados
    setDados(prev => ({
      ...prev,
      [tipoKey]: [...prev[tipoKey], item]
    }))

    setModalAberto(false)
    setNovoItem({ pa: '', credito: '', valor: '', perDcomp: '' })
  }

  const getCardConfig = (tipo: string) => {
    const configs = {
      pis: { color: 'blue', label: 'PIS/PASEP' },
      cofins: { color: 'green', label: 'COFINS' },
      irpj: { color: 'purple', label: 'IRPJ' },
      csll: { color: 'orange', label: 'CSLL' }
    }
    return configs[tipo as keyof typeof configs]
  }

  const renderCard = (tipo: string, items: CompensacaoItem[], total: number) => {
    const config = getCardConfig(tipo)

    return (
      <Card key={tipo} className={`border-${config.color}-200`}>
        <CardHeader className={`bg-${config.color}-50`}>
          <div className="flex justify-between items-center">
            <CardTitle className={`flex items-center gap-2 text-${config.color}-800`}>
              <Calculator className="h-5 w-5" />
              Detalhamento {config.label}
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => abrirModal(tipo)}
              className={`border-${config.color}-300 text-${config.color}-700 hover:bg-${config.color}-100`}
            >
              <Plus className="h-4 w-4 mr-1" />
              Adicionar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-3">
            {items.map((item, index) => (
              <div key={index} className={`flex justify-between items-center p-3 bg-${config.color}-50 rounded-lg`}>
                <div>
                  <p className={`font-medium text-${config.color}-800`}>{item.pa}</p>
                  <p className={`text-xs text-${config.color}-600`}>{item.perDcomp}</p>
                </div>
                <div className="text-right">
                  <p className={`font-bold text-${config.color}-700`}>{formatarMoeda(item.valor)}</p>
                  <Badge variant="secondary" className={`text-xs bg-${config.color}-100 text-${config.color}-700`}>
                    {item.credito}
                  </Badge>
                </div>
              </div>
            ))}
            <Separator />
            <div className={`flex justify-between items-center font-bold text-${config.color}-800`}>
              <span>SUBTOTAL {config.label}</span>
              <span className="text-lg">{formatarMoeda(total)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="mt-12 mb-8">
      <InfoIcmsExclusao />
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">RELATÓRIO DE COMPENSAÇÃO</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportarRelatorio} disabled={exportando}>
            <Download className="h-4 w-4 mr-2" />
            {exportando ? 'Exportando...' : 'Exportar Relatório'}
          </Button>
          <Button variant="outline" size="sm">
            <Printer className="h-4 w-4 mr-2" />
            Imprimir
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderCard('pis', dados.pis, totalPis)}
        {renderCard('cofins', dados.cofins, totalCofins)}
        {renderCard('irpj', dados.irpj, totalIrpj)}
        {renderCard('csll', dados.csll, totalCsll)}
      </div>

      {/* Total Final */}
      <Card className="mt-6 border-2 border-purple-200">
        <CardContent className="pt-6">
          <div className="text-center space-y-2">
            <p className="text-lg font-semibold text-gray-700">TOTAL COMPENSADO</p>
            <p className="text-4xl font-bold text-primary-color">{formatarMoeda(totalGeral)}</p>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>
                PIS/PASEP: {formatarMoeda(totalPis)} + COFINS: {formatarMoeda(totalCofins)}
              </p>
              <p>
                IRPJ: {formatarMoeda(totalIrpj)} + CSLL: {formatarMoeda(totalCsll)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Próximos Passos */}
      <Card className="mt-6 border-amber-200 bg-amber-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Info className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-amber-800 mb-2">Próximos Passos</h3>
              <div className="space-y-2 text-sm text-amber-700">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-amber-500 rounded-full"></div>
                  <span>Enviar este relatório para a contabilidade</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-amber-500 rounded-full"></div>
                  <span>Informar os valores na DCTFWeb</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-amber-500 rounded-full"></div>
                  <span>Aguardar confirmação da compensação</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal para adicionar item */}
      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar {tipoSelecionado.toUpperCase()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pa">Período de Apuração (PA)</Label>
              <Input
                id="pa"
                placeholder="Ex: 01/2024"
                value={novoItem.pa}
                onChange={e => setNovoItem(prev => ({ ...prev, pa: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="credito">Tipo de Crédito</Label>
              <Select
                value={novoItem.credito}
                onValueChange={value => setNovoItem(prev => ({ ...prev, credito: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PIS/PASEP">PIS/PASEP</SelectItem>
                  <SelectItem value="COFINS">COFINS</SelectItem>
                  <SelectItem value="IRPJ">IRPJ</SelectItem>
                  <SelectItem value="CSLL">CSLL</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="valor">Valor</Label>
              <Input
                id="valor"
                type="number"
                step="0.01"
                placeholder="0,00"
                value={novoItem.valor}
                onChange={e => setNovoItem(prev => ({ ...prev, valor: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="perDcomp">Per/Dcomp</Label>
              <Input
                id="perDcomp"
                placeholder="Ex: 12345.67890.150425.1.3.04-1234"
                value={novoItem.perDcomp}
                onChange={e => setNovoItem(prev => ({ ...prev, perDcomp: e.target.value }))}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setModalAberto(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={adicionarItem} className="flex-1">
                Adicionar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
